import { Status } from "../enums/Company";
import Role, { ROLE_RANK, isMyOnlyRole } from "../enums/Role";
import { LastSystem } from "./lastSystem";

/**
 * App identity keys, mirroring APP_FEATURE_KEYS in shared/src/utils/Constants.js.
 * BYOC is its own app plan but renders the Spend Management surface: it is the variant
 * that carries no card issuer application, so a BYOC company skips KYC/KYB entirely.
 */
export const APP_FEATURE_KEY = {
  SPEND: "feature_app_spend",
  BYOC: "feature_app_byoc",
  PLATFORM: "feature_app_platform",
} as const;

export type AppRoutingContext = {
  features: Record<string, boolean | undefined>;
  status?: string;
  /** The role ProtectedRoute checks (the user query's), so the two agree on a gate. */
  role?: string;
  isAdminOrHigher: boolean;
  isManagerOrLower: boolean;
  /** The user lives in /my alone (see isMyOnlyRole). */
  isMyOnly: boolean;
  isPlatformAdminOrHigher: boolean;
  lastSystem?: LastSystem | null;
  isAuditorOrHigher?: boolean;
};

type AppEntry = {
  key: LastSystem;
  /** Any one of these app identity keys entitles the app's UI. */
  featureKeys: readonly string[];
  resolveRoute(ctx: AppRoutingContext): string | null;
};

const APPS: AppEntry[] = [
  {
    key: "spend",
    featureKeys: [APP_FEATURE_KEY.SPEND, APP_FEATURE_KEY.BYOC],
    resolveRoute(ctx) {
      // Onboarding belongs to Spend alone. A BYOC-only company is created ACTIVE with no
      // KYB to complete, so it must never be routed into the onboarding flow — and if it
      // ever lands in a pending status, sending it there would be a dead end.
      const canOnboard = Boolean(ctx.features[APP_FEATURE_KEY.SPEND]);
      if (ctx.isAuditorOrHigher && ctx.status === Status.Active)
        return "/admin/dashboard";
      if (canOnboard && ctx.isAdminOrHigher && ctx.status === Status.Pending)
        return "/onboarding/start";
      if (
        canOnboard &&
        ctx.isAdminOrHigher &&
        ctx.status === Status.PendingReview
      )
        return "/pending";
      if (
        (ctx.isManagerOrLower || ctx.isMyOnly) &&
        ctx.status === Status.Active
      )
        return ctx.isMyOnly ? "/my/dashboard" : "/admin/transactions";
      return null;
    },
  },
  {
    key: "platform",
    featureKeys: [APP_FEATURE_KEY.PLATFORM],
    resolveRoute(ctx) {
      if (!ctx.isPlatformAdminOrHigher) return null;
      return "/platform/dashboard";
    },
  },
];

/**
 * The app identity keys that grant an app, any one of which is sufficient.
 * `readonly` so a caller cannot mutate the entry in APPS and change routing globally.
 */
export function appFeatureKeys(key: LastSystem): readonly string[] {
  return APPS.find((a) => a.key === key)?.featureKeys ?? [];
}

/** Where an admin impersonating `role` starts: the landing of the route tree that role gets. */
export function impersonationLanding(role?: string | null): string {
  if (isMyOnlyRole(role)) return "/my/dashboard";
  return ROLE_RANK[role as Role] >= ROLE_RANK[Role.Manager]
    ? "/admin/transactions"
    : "/admin/dashboard";
}

// The /my page each employee-tree /admin page has.
const MY_PAGE_FOR_ADMIN_PAGE = new Map([
  ["transactions", "/my/transactions"],
  ["expenses", "/my/transactions"],
  ["reimbursements", "/my/reimbursements"],
  ["expenses/reimbursements", "/my/reimbursements"],
  ["receipt-library", "/my/receipt-library"],
  ["cards", "/my/cards"],
  ["user-notifications", "/my/notifications"],
]);

/** A /my-only user's /admin link, opened on the same page in /my with its query (`?id=`). */
export function myPathForAdminPath(pathname: string, search = ""): string {
  const page = pathname.replace(/^\/admin\/?/, "").replace(/\/$/, "");
  const target =
    MY_PAGE_FOR_ADMIN_PAGE.get(page) ??
    (page.startsWith("expenses/") ? "/my/transactions" : "/my/dashboard");
  return `${target}${search}`;
}

/** What a guarded area requires. ProtectedRoute enforces it and the landing resolver checks it. */
export type RouteGate = {
  companyStatus: Status | readonly Status[];
  /** Exact roles allowed; omitted means any role. */
  roles?: readonly Role[];
  app?: LastSystem;
};

export const ROUTE_GATES = {
  active: { companyStatus: Status.Active },
  onboarding: { companyStatus: Status.Pending },
  /** An active company verifying itself with a second issuer onboards too. */
  issuerOnboarding: { companyStatus: [Status.Pending, Status.Active] },
  spend: { companyStatus: Status.Active, app: "spend" },
  owner: { companyStatus: Status.Active, roles: [Role.Owner] },
  platform: {
    companyStatus: Status.Active,
    roles: [Role.PlatformOwner, Role.PlatformAdmin, Role.SuperAdmin],
    app: "platform",
  },
} as const satisfies Record<string, RouteGate>;

/** The gate App.tsx puts in front of each landing route, strictest across the role route trees. */
const LANDING_GATES: ReadonlyArray<[prefix: string, gate: RouteGate]> = [
  ["/admin/", ROUTE_GATES.spend],
  ["/onboarding/", ROUTE_GATES.onboarding],
  ["/platform/", ROUTE_GATES.platform],
  ["/my/", ROUTE_GATES.active],
];

export function meetsGate(
  gate: RouteGate,
  subject: { status?: string; role?: string },
): boolean {
  const statuses: readonly Status[] = ([] as Status[]).concat(
    gate.companyStatus,
  );
  if (!statuses.some((status) => status === subject.status)) return false;
  return !gate.roles || gate.roles.some((role) => role === subject.role);
}

export function lacksGateApp(
  gate: RouteGate,
  hasFeature: (key: string) => boolean,
): boolean {
  return gate.app !== undefined && !appFeatureKeys(gate.app).some(hasFeature);
}

function landingGatePasses(route: string, ctx: AppRoutingContext): boolean {
  const gate = LANDING_GATES.find(([prefix]) => route.startsWith(prefix))?.[1];
  return (
    !gate ||
    (meetsGate(gate, ctx) &&
      !lacksGateApp(gate, (key) => Boolean(ctx.features[key])))
  );
}

/**
 * A platform user signs in to the platform application, so the account must have it.
 * Unknown keys mean "not loaded yet" and must not withhold a legitimate choice;
 * assertCanAssignRoleInCompany on the server is what actually enforces this.
 */
export function hasPlatformApp(
  appFeatureKeys?: ReadonlySet<string> | null,
): boolean {
  return !appFeatureKeys || appFeatureKeys.has(APP_FEATURE_KEY.PLATFORM);
}

export function entitledApps(
  features: AppRoutingContext["features"],
): AppEntry[] {
  return APPS.filter((a) => a.featureKeys.some((key) => features[key]));
}

export function resolveLandingRoute(ctx: AppRoutingContext): string | null {
  const apps = entitledApps(ctx.features);
  if (apps.length === 0) return null;
  if (ctx.status === Status.Inactive) return "/inactive";
  if (ctx.status === Status.Declined) return "/declined";
  const preferred = apps.find((a) => a.key === ctx.lastSystem);
  const ordered = preferred
    ? [preferred, ...apps.filter((a) => a !== preferred)]
    : apps;
  for (const app of ordered) {
    const route = app.resolveRoute(ctx);
    // A route its own guard would refuse sends the user back here, forever.
    if (route && landingGatePasses(route, ctx)) return route;
  }
  return null;
}

export function appKeyForRoute(pathname: string): LastSystem | null {
  if (pathname.startsWith("/platform")) return "platform";
  if (pathname.startsWith("/admin") || pathname.startsWith("/onboarding")) {
    return "spend";
  }
  return null;
}
